import { Link } from "react-router-dom";
import { useState } from "react";
import Footer from "@/components/Footer";

/**
 * Public Developer API docs page — no auth required so franchisees /
 * white-label licensees can self-serve integration. Every example uses
 * curl + shows expected shape so devs can validate before writing SDK code.
 */
export default function ApiDocs() {
  const BASE = process.env.REACT_APP_BACKEND_URL || "https://your-tenant.checkmatepayrolls.com";
  const [buddyOpen, setBuddyOpen] = useState(false);
  const [buddyMsg, setBuddyMsg] = useState("");
  const [buddyLog, setBuddyLog] = useState([
    { role: "buddy", text: "Enterprise-Grade · Ask me anything about the Developer API — I can explain scopes, curl examples, and how to filter results." },
  ]);
  const [buddyBusy, setBuddyBusy] = useState(false);
  const askBuddy = async () => {
    const msg = buddyMsg.trim();
    if (!msg) return;
    setBuddyLog((log) => [...log, { role: "user", text: msg }]);
    setBuddyMsg("");
    setBuddyBusy(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${base}/api/buddy/chat/public`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: "docs-widget" }),
      });
      const data = await res.json();
      const reply = data.reply || data.detail || "Ping support@checkmatepayrolls.com for help.";
      setBuddyLog((log) => [...log, { role: "buddy", text: reply }]);
    } catch (e) {
      setBuddyLog((log) => [...log, { role: "buddy", text: `Sorry — I'm offline right now. Email support@checkmatepayrolls.com.` }]);
    }
    setBuddyBusy(false);
  };
  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b-4 border-ink bg-ink text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-8 flex items-center justify-between">
          <div>
            <div className="overline text-warn mb-2">§ CHECKMATE DEVELOPER API · v1</div>
            <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">White-Label Integration Docs</h1>
            <p className="font-mono text-sm text-white/70 mt-2 max-w-2xl">Read-only endpoints for timesheets, payroll stubs, and employee records — Bearer-token authenticated, per-company scoped. Mint keys in Company Settings → Developer API.</p>
          </div>
          <Link to="/" className="border-2 border-white px-4 py-2 font-mono uppercase text-xs tracking-widest hover:bg-white hover:text-ink" data-testid="docs-home-link">← Home</Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-12 space-y-10" data-testid="api-docs-main">

        <section>
          <div className="overline mb-3">§ AUTHENTICATION</div>
          <p className="font-mono text-sm mb-3 max-w-3xl">Every v1 endpoint requires an <code className="bg-secondary px-1">Authorization: Bearer &lt;key&gt;</code> header. Keys are minted in Company Settings → Developer API, hashed at rest with bcrypt, and shown in plaintext exactly once at creation.</p>
          <pre className="bg-ink text-white font-mono text-xs p-4 overflow-x-auto"><code>{`# Sample header on every request
Authorization: Bearer ck_<companyPrefix>_<randomToken>`}</code></pre>
          <div className="mt-3 border-2 border-warn bg-warn/10 p-3 font-mono text-xs">
            <b>Scopes:</b> Each key is scoped to a subset of <code>timesheets:read</code>, <code>payroll:read</code>, <code>employees:read</code>. Requests to endpoints outside a key's scope return <code>403 Forbidden</code>.
          </div>
        </section>

        <Endpoint
          method="GET"
          path="/api/v1/timesheets"
          scope="timesheets:read"
          desc="Every clock-in/out record for your company. Supports optional period_start/period_end (YYYY-MM-DD) and limit (max 2000)."
          curl={`curl "${BASE}/api/v1/timesheets?period_start=2026-01-01&period_end=2026-01-31&limit=100" \\\n  -H "Authorization: Bearer $CHECKMATE_KEY"`}
          response={`{
  "count": 42,
  "items": [
    {
      "id": "6a55…",
      "employee_id": "6a44…",
      "employee_name": "Jordan Rivera",
      "worksite_id": "6a33…",
      "worksite_name": "SF HQ",
      "clock_in_at": "2026-01-15T09:02:33+00:00",
      "clock_out_at": "2026-01-15T17:33:12+00:00",
      "hours": 8.51,
      "flagged": false,
      "approval_status": "approved"
    }
  ]
}`}
        />

        <Endpoint
          method="GET"
          path="/api/v1/payroll/stubs"
          scope="payroll:read"
          desc="Every pay stub issued in your company. Optional year filter (integer, e.g. 2026). Encrypted fields (SSN) are stripped."
          curl={`curl "${BASE}/api/v1/payroll/stubs?year=2026&limit=500" \\\n  -H "Authorization: Bearer $CHECKMATE_KEY"`}
          response={`{
  "count": 61,
  "items": [
    {
      "id": "6a66…",
      "employee_id": "6a44…",
      "pay_date": "2026-01-31",
      "period_start": "2026-01-15",
      "period_end": "2026-01-29",
      "gross": 3200.00,
      "federal_tax": 289.44,
      "social_security": 198.40,
      "medicare": 46.40,
      "state_tax": 176.00,
      "wc_premium_employer": 40.00,
      "garnishments_total": 320.00,
      "net_pay": 2169.76
    }
  ]
}`}
        />

        <Endpoint
          method="GET"
          path="/api/v1/employees"
          scope="employees:read"
          desc="Every active + terminated employee. SSN + bank account fields are stripped from the response."
          curl={`curl "${BASE}/api/v1/employees" \\\n  -H "Authorization: Bearer $CHECKMATE_KEY"`}
          response={`{
  "count": 86,
  "items": [
    {
      "id": "6a44…",
      "first_name": "Jordan",
      "last_name": "Rivera",
      "email": "j.rivera@example.com",
      "status": "active",
      "employment_type": "hourly",
      "hourly_rate": 40.0,
      "state": "CA"
    }
  ]
}`}
        />

        <section>
          <div className="overline mb-3">§ ERRORS</div>
          <ul className="font-mono text-sm space-y-2">
            <li><code className="bg-secondary px-1">401 Unauthorized</code> — Missing / malformed / revoked Bearer token.</li>
            <li><code className="bg-secondary px-1">403 Forbidden</code> — Key does not include the required scope for that endpoint.</li>
            <li><code className="bg-secondary px-1">429 Too Many Requests</code> — Rate-limited (planned) — retry with exponential backoff.</li>
          </ul>
        </section>

        <section className="border-t-4 border-ink pt-8">
          <div className="overline mb-3">§ NEED HELP?</div>
          <p className="font-mono text-sm">Email <a href="mailto:support@checkmatepayrolls.com" className="underline">support@checkmatepayrolls.com</a> or ping the founder at <a href="mailto:paradox.gilbertbent@gmail.com" className="underline">paradox.gilbertbent@gmail.com</a>. All Enterprise-Grade licensees get response within 1 business day.</p>
        </section>
      </main>

      <footer className="border-t-4 border-ink bg-ink text-white/70 py-6 mt-16">
        <div className="max-w-[1200px] mx-auto px-6 font-mono text-xs flex items-center justify-between">
          <div>© {new Date().getFullYear()} Checkmate Payroll</div>
          <div className="flex gap-4"><Link to="/legal/terms" className="hover:text-white">Terms</Link><Link to="/legal/privacy" className="hover:text-white">Privacy</Link></div>
        </div>
      </footer>

      {/* Docs-page Checkmate Buddy — public unauthenticated variant */}
      {buddyOpen ? (
        <div className="fixed bottom-4 right-4 w-96 max-w-[calc(100vw-2rem)] h-[520px] bg-white border-4 border-ink shadow-hard-lg flex flex-col z-50" data-testid="docs-buddy-panel">
          <div className="border-b-2 border-ink px-4 py-3 flex items-center justify-between bg-ink text-white">
            <div>
              <div className="overline text-warn">§ CHECKMATE BUDDY · DEV</div>
              <div className="font-heading font-black">Ask about the API</div>
            </div>
            <button onClick={() => setBuddyOpen(false)} data-testid="docs-buddy-close" className="border-2 border-white text-white px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-white hover:text-ink">Close ×</button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="docs-buddy-log">
            {buddyLog.map((m, i) => (
              <div key={i} className={`font-mono text-xs p-2 border-2 ${m.role === "user" ? "border-ink bg-secondary ml-8" : "border-good bg-good/10 mr-8"}`}>{m.text}</div>
            ))}
            {buddyBusy && <div className="font-mono text-[10px] text-muted-foreground">Thinking…</div>}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); askBuddy(); }} className="border-t-2 border-ink p-2 flex gap-2">
            <input data-testid="docs-buddy-input" value={buddyMsg} onChange={(e) => setBuddyMsg(e.target.value)} placeholder="How do I filter by employee?" className="flex-1 px-3 py-2 border-2 border-ink font-mono text-xs" />
            <button type="submit" data-testid="docs-buddy-send" disabled={buddyBusy} className="hard-btn px-3 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-50">Ask</button>
          </form>
        </div>
      ) : (
        <button onClick={() => setBuddyOpen(true)} data-testid="docs-buddy-open" className="fixed bottom-4 right-4 hard-btn px-4 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 z-40 shadow-hard-lg">
          💬 Ask Checkmate Buddy
        </button>
      )}
      <Footer />
    </div>
  );
}

function Endpoint({ method, path, scope, desc, curl, response }) {
  const testid = `endpoint-${path.replace(/[^a-z0-9]/gi, "-")}`;
  const [liveResult, setLiveResult] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [key, setKey] = useState(() => {
    // sessionStorage, not localStorage: a live API key must not outlive the tab.
    try { return sessionStorage.getItem("checkmate_dev_key") || ""; }
    catch { return ""; }
  });
  const runLive = async () => {
    if (!key.trim()) { setLiveResult({ error: "Paste your API key first (starts with ck_)." }); return; }
    try { sessionStorage.setItem("checkmate_dev_key", key.trim()); }
    catch (err) { console.warn("could not cache the API key for this tab", err); }
    setLiveBusy(true);
    setLiveResult(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${base}${path}${path.includes("?") ? "&" : "?"}limit=3`, {
        headers: { "Authorization": `Bearer ${key.trim()}` },
      });
      const data = await res.json();
      setLiveResult({ status: res.status, data });
    } catch (e) {
      setLiveResult({ error: String(e?.message || e) });
    }
    setLiveBusy(false);
  };
  return (
    <section className="border-2 border-ink bg-white p-5" data-testid={testid}>
      <div className="flex items-baseline gap-3 flex-wrap mb-3">
        <span className="px-2 py-1 bg-good text-white font-mono text-xs font-black uppercase tracking-widest">{method}</span>
        <code className="font-mono text-lg font-black">{path}</code>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Scope: <b>{scope}</b></span>
      </div>
      <p className="font-mono text-xs text-muted-foreground mb-3">{desc}</p>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <div className="overline mb-1">Request (curl)</div>
          <pre className="bg-ink text-white font-mono text-[11px] p-3 overflow-x-auto whitespace-pre"><code>{curl}</code></pre>
        </div>
        <div>
          <div className="overline mb-1">Response · 200 OK</div>
          <pre className="bg-secondary font-mono text-[11px] p-3 overflow-x-auto whitespace-pre"><code>{response}</code></pre>
        </div>
      </div>
      <div className="mt-4 border-t-2 border-ink pt-3">
        <div className="overline mb-2 flex items-center gap-2">§ LIVE TEST
          <span className="ml-1 px-1.5 py-0.5 bg-warn text-ink text-[9px] font-black uppercase tracking-widest">Try It</span>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <input
            data-testid={`${testid}-key-input`}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Paste your ck_… API key here"
            className="flex-1 min-w-[200px] max-w-md px-3 py-2 border-2 border-ink font-mono text-xs"
            type="password"
          />
          <button
            data-testid={`${testid}-run-live`}
            onClick={runLive}
            disabled={liveBusy}
            className="hard-btn px-4 py-2 font-mono uppercase text-xs tracking-widest disabled:opacity-50"
          >{liveBusy ? "Calling…" : "Run Live →"}</button>
        </div>
        {liveResult && (
          <pre className={`mt-3 font-mono text-[11px] p-3 overflow-x-auto whitespace-pre max-h-64 border-2 ${liveResult.error || liveResult.status >= 400 ? "border-bad bg-bad/10" : "border-good bg-good/10"}`} data-testid={`${testid}-live-result`}>
            <code>{liveResult.error ? `Error: ${liveResult.error}` : `HTTP ${liveResult.status}\n${JSON.stringify(liveResult.data, null, 2).slice(0, 2000)}`}</code>
          </pre>
        )}
      </div>
    </section>
  );
}
